import React, { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Switch } from "@/components/ui/switch";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { Plus, Trash2, Edit2, Check, X, BarChart3, ChevronUp, ChevronDown, CheckCircle2, Loader2, GripVertical, Folder, FileText, Sparkles } from "lucide-react";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { PageTitle } from "@/components/PageTitle";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator } from "@/components/ui/dropdown-menu";
import { DndContext, DragEndEvent, DragStartEvent, DragOverlay, useDraggable, useDroppable, DragOverEvent, PointerSensor, useSensor, useSensors, closestCenter } from "@dnd-kit/core";
import { SortableContext, useSortable, verticalListSortingStrategy, arrayMove } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { cn } from "@/lib/utils";
import { Skeleton } from "@/components/ui/skeleton";

interface Subcategory {
  id: string;
  name: string;
  show_chart: boolean;
  is_auto_created: boolean;
}

interface Category {
  id: string;
  name: string;
  display_order: number;
  is_system: boolean;
  subcategories: Subcategory[];
}

// Draggable Subcategory Component
function DraggableSubcategory({ 
  subcategory, 
  categoryId,
  isOver,
  editingSubcategory,
  editingName,
  setEditingName,
  setEditingSubcategory,
  updateSubcategoryName,
  toggleShowChart,
  deleteSubcategory 
}: { 
  subcategory: Subcategory;
  categoryId: string;
  isOver: boolean;
  editingSubcategory: string | null;
  editingName: string;
  setEditingName: (name: string) => void;
  setEditingSubcategory: (id: string | null) => void;
  updateSubcategoryName: (id: string) => void;
  toggleShowChart: (id: string, value: boolean) => void;
  deleteSubcategory: (id: string, name: string) => void;
}) {
  const { attributes, listeners, setNodeRef, transform, isDragging } = useDraggable({
    id: subcategory.id,
    data: { subcategory, categoryId }
  });

  const style = transform ? {
    transform: `translate3d(${transform.x}px, ${transform.y}px, 0)`,
  } : undefined;

  return (
    <div 
      ref={setNodeRef}
      style={style}
      className={cn(
        "flex flex-col sm:flex-row sm:items-center justify-between gap-3 p-3 border rounded-lg transition-all overflow-hidden",
        isDragging && "opacity-40 scale-95",
        !isDragging && "hover:bg-accent/50"
      )}
    >
      {editingSubcategory === subcategory.id ? (
        <div className="flex items-center gap-2 flex-1 min-w-0">
          <Input
            value={editingName}
            onChange={(e) => setEditingName(e.target.value)}
            className="flex-1 min-w-0"
            autoFocus
            onKeyDown={(e) => {
              if (e.key === 'Enter') updateSubcategoryName(subcategory.id);
              if (e.key === 'Escape') setEditingSubcategory(null);
            }}
          />
          <Button
            size="sm"
            variant="ghost"
            onClick={() => updateSubcategoryName(subcategory.id)}
            className="shrink-0"
          >
            <Check className="h-4 w-4" />
          </Button>
          <Button
            size="sm"
            variant="ghost"
            onClick={() => setEditingSubcategory(null)}
            className="shrink-0"
          >
            <X className="h-4 w-4" />
          </Button>
        </div>
      ) : (
        <>
          <div className="flex items-center gap-2 min-w-0 flex-1 overflow-hidden">
            <div 
              {...listeners} 
              {...attributes}
              className="cursor-grab active:cursor-grabbing touch-none shrink-0"
            >
              <GripVertical className="h-4 w-4 text-muted-foreground" />
            </div>
            <span className="font-medium truncate max-w-[200px] sm:max-w-none">{subcategory.name}</span>
            {subcategory.is_auto_created && (
              <span className="text-xs text-muted-foreground px-2 py-0.5 bg-muted rounded shrink-0">
                Auto
              </span>
            )}
          </div>
          <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3 sm:gap-4 shrink-0">
            <div className="flex items-center gap-2">
              <Switch
                checked={subcategory.show_chart}
                onCheckedChange={() => toggleShowChart(subcategory.id, subcategory.show_chart)}
              />
              <span className="text-sm text-muted-foreground whitespace-nowrap">
                {subcategory.show_chart ? "Gráfico ativo" : "Exibir gráfico"}
              </span>
            </div>
            <div className="flex gap-1">
              <Button
                size="sm"
                variant="ghost"
                onClick={() => {
                  setEditingSubcategory(subcategory.id);
                  setEditingName(subcategory.name);
                }}
              >
                <Edit2 className="h-4 w-4" />
              </Button>
              <Button
                size="sm"
                variant="ghost"
                onClick={() => deleteSubcategory(subcategory.id, subcategory.name)}
              >
                <Trash2 className="h-4 w-4" />
              </Button>
            </div>
          </div>
        </>
      )}
    </div>
  );
}

// Draggable Category Badge (Mobile)
function DraggableCategoryBadge({
  category,
  isSelected,
  onClick,
}: {
  category: Category;
  isSelected: boolean;
  onClick: () => void;
}) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: category.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      onClick={onClick}
      className={cn(
        "shrink-0 px-3 py-2 cursor-pointer transition-colors whitespace-nowrap inline-flex items-center rounded-full border text-sm font-medium",
        isSelected
          ? "bg-primary text-primary-foreground hover:bg-primary/90 border-primary"
          : "hover:bg-accent border-border",
        isDragging && "opacity-50"
      )}
    >
      <div
        {...attributes}
        {...listeners}
        className="cursor-grab active:cursor-grabbing touch-none px-2 py-2"
        onClick={(e) => e.stopPropagation()}
      >
        <GripVertical className="h-3.5 w-3.5" />
      </div>
      <div className="flex items-center px-2 py-2 flex-1">
        <Folder className="h-3.5 w-3.5 mr-1.5" />
        <span className="font-medium">{category.name}</span>
        <span className="ml-1.5 text-xs opacity-70">({category.subcategories.length})</span>
      </div>
    </div>
  );
}

// Draggable Category Item (Desktop Sidebar)
function DraggableCategoryItem({
  category,
  isSelected,
  onClick,
}: {
  category: Category;
  isSelected: boolean;
  onClick: () => void;
}) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: category.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={cn(
        "flex items-start gap-2 px-3 py-2.5 rounded-lg transition-colors w-full",
        isSelected
          ? "bg-primary text-primary-foreground"
          : "hover:bg-accent",
        isDragging && "opacity-50"
      )}
    >
      <div
        {...attributes}
        {...listeners}
        className="cursor-grab active:cursor-grabbing touch-none shrink-0 mt-0.5"
        onClick={(e) => e.stopPropagation()}
      >
        <GripVertical className="h-4 w-4" />
      </div>
      <button
        onClick={onClick}
        className="flex items-start gap-2 flex-1 min-w-0 text-left"
      >
        <Folder className="h-4 w-4 shrink-0 mt-0.5" />
        <div className="flex flex-col gap-0.5 flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <span className="font-medium truncate">{category.name}</span>
            {category.is_system && (
              <span className="text-[10px] px-1.5 py-0.5 bg-background/20 rounded shrink-0">
                Sistema
              </span>
            )}
          </div>
          <span className="text-xs opacity-70">
            {category.subcategories.length} sub
          </span>
        </div>
      </button>
    </div>
  );
}

// Droppable Category Card
function DroppableCategory({ 
  category, 
  isOver,
  children 
}: { 
  category: Category;
  isOver: boolean;
  children: React.ReactNode;
}) {
  const { setNodeRef } = useDroppable({
    id: category.id,
    data: { category }
  });

  return (
    <Card 
      ref={setNodeRef}
      className={cn(
        "transition-all duration-200",
        isOver && !category.is_system && "ring-2 ring-primary bg-primary/5"
      )}
    >
      {children}
    </Card>
  );
}

export default function Categorias() {
  const [categories, setCategories] = useState<Category[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedCategoryId, setSelectedCategoryId] = useState<string | null>(null);
  const [editingSubcategory, setEditingSubcategory] = useState<string | null>(null);
  const [editingName, setEditingName] = useState("");
  const [addingToCategory, setAddingToCategory] = useState<string | null>(null);
  const [newSubcategoryName, setNewSubcategoryName] = useState("");
  const [editingCategory, setEditingCategory] = useState<string | null>(null);
  const [editingCategoryName, setEditingCategoryName] = useState("");
  const [editingCategoryOrder, setEditingCategoryOrder] = useState<number>(0);
  const [addingCategory, setAddingCategory] = useState(false);
  const [newCategoryName, setNewCategoryName] = useState("");
  const [newCategoryOrder, setNewCategoryOrder] = useState<number>(0);
  const [deletingCategory, setDeletingCategory] = useState<Category | null>(null);
  const [creatingCategory, setCreatingCategory] = useState(false);
  const [justCreatedCategoryId, setJustCreatedCategoryId] = useState<string | null>(null);
  const [activeSubcategory, setActiveSubcategory] = useState<Subcategory & { categoryId: string } | null>(null);
  const [activeCategory, setActiveCategory] = useState<Category | null>(null);
  const [overId, setOverId] = useState<string | null>(null);
  const [dragType, setDragType] = useState<'category' | 'subcategory' | null>(null);
  const [creatingTemplate, setCreatingTemplate] = useState(false);
  const [templates, setTemplates] = useState<Array<{ id: string; title: string; health_area_id: string; health_area?: { name: string }; items: Array<{ id: string; name: string; is_active: boolean; display_order: number }> }>>([]);
  const [userHealthAreaId, setUserHealthAreaId] = useState<string | null>(null);
  const [loadingTemplates, setLoadingTemplates] = useState(true);
  const { toast } = useToast();

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8,
      },
    })
  );

  useEffect(() => {
    loadUserHealthArea();
    loadCategories();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (userHealthAreaId) {
      loadTemplates();
    }
  }, [userHealthAreaId]);

  // Set first category as selected when categories are loaded
  useEffect(() => {
    if (categories.length > 0 && !selectedCategoryId) {
      setSelectedCategoryId(categories[0].id);
    }
  }, [categories, selectedCategoryId]);

  // Keyboard shortcuts
  useEffect(() => {
    const handleKeyPress = (e: KeyboardEvent) => {
      // Ctrl+N para criar nova categoria
      if ((e.ctrlKey || e.metaKey) && e.key === 'n' && !addingCategory) {
        e.preventDefault();
        setAddingCategory(true);
        setNewCategoryName("");
        setNewCategoryOrder(0);
      }
      // Esc to cancel
      if (e.key === 'Escape' && addingCategory) {
        setAddingCategory(false);
        setNewCategoryName("");
        setNewCategoryOrder(0);
      }
    };
    
    window.addEventListener('keydown', handleKeyPress);
    return () => window.removeEventListener('keydown', handleKeyPress);
  }, [addingCategory]);

  async function loadUserHealthArea() {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      const { data: profile } = await supabase
        .from('profiles')
        .select('health_area_id')
        .eq('id', user.id)
        .single();

      if (profile?.health_area_id) {
        setUserHealthAreaId(profile.health_area_id);
      }
    } catch (error) {
      console.error('Error loading user health area:', error);
    }
  }

  async function loadTemplates() {
    try {
      setLoadingTemplates(true);
      if (!userHealthAreaId) return;

      const { data, error } = await supabase
        .from('category_templates')
        .select(`
          id,
          title,
          health_area_id,
          health_areas:health_area_id (
            id,
            name
          ),
          items:category_template_items (
            id,
            name,
            is_active,
            display_order
          )
        `)
        .eq('health_area_id', userHealthAreaId)
        .order('created_at', { ascending: false });

      if (error) throw error;

      const formattedTemplates = (data || []).map((template: any) => ({
        ...template,
        health_area: template.health_areas,
        items: (template.items || [])
          .filter((item: any) => item.is_active)
          .sort((a: any, b: any) => a.display_order - b.display_order)
      }));

      setTemplates(formattedTemplates);
    } catch (error) {
      console.error('Error loading templates:', error);
      toast({
        title: "Erro ao carregar templates",
        description: "Não foi possível carregar os templates disponíveis.",
        variant: "destructive",
      });
    } finally {
      setLoadingTemplates(false);
    }
  }

  async function loadCategories() {
    try {
      // Obter o user_id atual
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        console.error('No user logged in');
        setLoading(false);
        return;
      }

      const { data, error } = await supabase
        .from('lab_categories')
        .select(`
          id,
          name,
          display_order,
          is_system,
          subcategories:lab_subcategories(
            id,
            name,
            show_chart,
            is_auto_created
          )
        `)
        .eq('user_id', user.id) // Filtrar explicitamente por user_id
        .order('display_order', { ascending: true });

      if (error) throw error;

      setCategories(data as Category[]);
    } catch (error) {
      console.error('Error loading categories:', error);
      toast({
        title: "Erro ao carregar categorias",
        description: "Não foi possível carregar as categorias.",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  }

  async function toggleShowChart(subcategoryId: string, currentValue: boolean) {
    try {
      const { error } = await supabase
        .from('lab_subcategories')
        .update({ show_chart: !currentValue })
        .eq('id', subcategoryId);

      if (error) throw error;

      await loadCategories();
      
      // Disparar evento para recarregar gráficos nos pacientes
      window.dispatchEvent(new CustomEvent('reload-health-indicators'));
      
      toast({
        title: currentValue ? "Gráfico desabilitado" : "Gráfico habilitado",
        description: currentValue 
          ? "Este indicador não será mais exibido em gráficos."
          : "Este indicador agora será exibido em gráficos de evolução.",
      });
    } catch (error) {
      console.error('Error toggling chart:', error);
      toast({
        title: "Erro ao atualizar configuração",
        variant: "destructive",
      });
    }
  }

  async function updateSubcategoryName(subcategoryId: string) {
    if (!editingName.trim()) {
      toast({
        title: "Nome inválido",
        description: "O nome da subcategoria não pode estar vazio.",
        variant: "destructive",
      });
      return;
    }

    try {
      const { error } = await supabase
        .from('lab_subcategories')
        .update({ name: editingName.trim() })
        .eq('id', subcategoryId);

      if (error) throw error;

      setEditingSubcategory(null);
      setEditingName("");
      await loadCategories();
      toast({
        title: "Subcategoria atualizada",
        description: "O nome foi alterado com sucesso.",
      });
    } catch (error) {
      console.error('Error updating subcategory:', error);
      toast({
        title: "Erro ao atualizar subcategoria",
        variant: "destructive",
      });
    }
  }

  async function deleteSubcategory(subcategoryId: string, subcategoryName: string) {
    if (!confirm(`Tem certeza que deseja excluir a subcategoria "${subcategoryName}"? Esta ação não pode ser desfeita.`)) {
      return;
    }

    try {
      const { error } = await supabase
        .from('lab_subcategories')
        .delete()
        .eq('id', subcategoryId);

      if (error) throw error;

      await loadCategories();
      toast({
        title: "Subcategoria excluída",
        description: "A subcategoria foi removida com sucesso.",
      });
    } catch (error) {
      console.error('Error deleting subcategory:', error);
      toast({
        title: "Erro ao excluir subcategoria",
        description: "Não foi possível excluir a subcategoria. Ela pode estar sendo usada em resultados.",
        variant: "destructive",
      });
    }
  }

  async function addSubcategory(categoryId: string) {
    if (!newSubcategoryName.trim()) {
      toast({
        title: "Nome inválido",
        description: "Digite um nome para a subcategoria.",
        variant: "destructive",
      });
      return;
    }

    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('User not found');

      const { data: newSubcategory, error } = await supabase
        .from('lab_subcategories')
        .insert({
          category_id: categoryId,
          user_id: user.id,
          name: newSubcategoryName.trim(),
          show_chart: false,
          is_auto_created: false,
          slug: '', // Trigger auto-gera do name
        })
        .select()
        .single();

      if (error) throw error;

      // Vincular resultados existentes que tenham o mesmo nome
      if (newSubcategory) {
        try {
          // Vincular resultados sem subcategoria (matching exato)
          const { error: linkError } = await supabase
            .from('lab_results_onboarding')
            .update({ subcategory_id: newSubcategory.id })
            .eq('test_name', newSubcategoryName.trim())
            .is('subcategory_id', null);

          if (linkError) {
            console.error('Error linking results to subcategory:', linkError);
          }

          // Chamar a função SQL para matching normalizado (vincula todos os resultados que batem com qualquer subcategoria)
          // Isso garante que resultados com nomes similares sejam vinculados
          const { error: functionError } = await supabase.rpc('link_exact_subcategories', {
            p_user_id: user.id,
            p_patient_id: null // null = todos os pacientes
          });

          if (functionError) {
            console.error('Error calling link_exact_subcategories:', functionError);
          }
        } catch (linkErr) {
          console.error('Error linking results:', linkErr);
          // Não falhar o fluxo se a vinculação der erro, apenas logar
        }
      }

      setAddingToCategory(null);
      setNewSubcategoryName("");
      await loadCategories();
      
      // Disparar evento para recarregar gráficos nos pacientes
      window.dispatchEvent(new CustomEvent('reload-health-indicators'));
      
      toast({
        title: "Subcategoria adicionada",
        description: "Nova subcategoria criada com sucesso.",
      });
    } catch (error) {
      console.error('Error adding subcategory:', error);
      toast({
        title: "Erro ao adicionar subcategoria",
        variant: "destructive",
      });
    }
  }

  async function createCategory() {
    if (!newCategoryName.trim()) {
      toast({
        title: "Nome inválido",
        description: "Digite um nome para a categoria.",
        variant: "destructive",
      });
      return;
    }

    setCreatingCategory(true);
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('User not found');

      // Get max display_order to add at the end
      const maxOrder = categories.length > 0 
        ? Math.max(...categories.map(c => c.display_order)) 
        : 0;

      const { data: newCategory, error } = await supabase
        .from('lab_categories')
        .insert({
          user_id: user.id,
          name: newCategoryName.trim(),
          display_order: newCategoryOrder || maxOrder + 1,
          is_system: false,
        })
        .select()
        .single();

      if (error) throw error;

      setAddingCategory(false);
      setNewCategoryName("");
      setNewCategoryOrder(0);
      setJustCreatedCategoryId(newCategory.id);
      await loadCategories();
      
      // Select the newly created category
      if (newCategory) {
        setSelectedCategoryId(newCategory.id);
      }
      
      toast({
        title: "Categoria criada",
        description: "Nova categoria criada com sucesso.",
      });
    } catch (error) {
      console.error('Error creating category:', error);
      toast({
        title: "Erro ao criar categoria",
        description: "Não foi possível criar a categoria.",
        variant: "destructive",
      });
    } finally {
      setCreatingCategory(false);
    }
  }

  async function createCategoryFromTemplate(templateId: string) {
    setCreatingTemplate(true);
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('User not found');

      // Buscar template do banco
      const template = templates.find(t => t.id === templateId);
      if (!template) {
        toast({
          title: "Template não encontrado",
          description: "O template selecionado não foi encontrado.",
          variant: "destructive",
        });
        setCreatingTemplate(false);
        return;
      }

      const categoryName = template.title;

      // Verificar se a categoria já existe
      const { data: existingCategory } = await supabase
        .from('lab_categories')
        .select('id')
        .eq('user_id', user.id)
        .eq('name', categoryName)
        .maybeSingle();

      if (existingCategory) {
        toast({
          title: "Categoria já existe",
          description: `A categoria "${categoryName}" já existe.`,
          variant: "destructive",
        });
        setCreatingTemplate(false);
        return;
      }

      // Get max display_order to add at the end
      const maxOrder = categories.length > 0 
        ? Math.max(...categories.map(c => c.display_order)) 
        : 0;

      // Criar a categoria
      const { data: newCategory, error: categoryError } = await supabase
        .from('lab_categories')
        .insert({
          user_id: user.id,
          name: categoryName,
          display_order: maxOrder + 1,
          is_system: false,
        })
        .select()
        .single();

      if (categoryError) throw categoryError;

      // Obter subcategorias do template (apenas itens ativos)
      const subcategoryNames = template.items.map(item => item.name);

      // Criar todas as subcategorias
      // Para templates, ativar show_chart = true por padrão para gerar gráficos automaticamente
      const subcategoriesToInsert = subcategoryNames.map(name => ({
        category_id: newCategory.id,
        user_id: user.id,
        name: name.trim(),
        show_chart: true, // Ativar gráficos por padrão para templates
        is_auto_created: false,
        slug: '', // Trigger auto-gera do name
      }));

      const { error: subcategoriesError } = await supabase
        .from('lab_subcategories')
        .insert(subcategoriesToInsert);

      if (subcategoriesError) throw subcategoriesError;

      // Vincular resultados existentes às novas subcategorias
      try {
        // Chamar a função SQL para matching (vincula todos os resultados que batem com as subcategorias)
        const { error: functionError } = await supabase.rpc('link_exact_subcategories', {
          p_user_id: user.id,
          p_patient_id: null // null = todos os pacientes
        });

        if (functionError) {
          console.error('Error calling link_exact_subcategories:', functionError);
          // Não falhar o fluxo se a vinculação der erro, apenas logar
        }
      } catch (linkErr) {
        console.error('Error linking results:', linkErr);
        // Não falhar o fluxo se a vinculação der erro, apenas logar
      }

      setJustCreatedCategoryId(newCategory.id);
      await loadCategories();
      
      // Select the newly created category
      setSelectedCategoryId(newCategory.id);
      
      toast({
        title: "Template criado com sucesso",
        description: `Categoria "${categoryName}" criada com ${subcategoryNames.length} subcategoria(s). Os dados existentes serão vinculados automaticamente.`,
      });
    } catch (error) {
      console.error('Error creating template:', error);
      toast({
        title: "Erro ao criar template",
        description: "Não foi possível criar o template.",
        variant: "destructive",
      });
    } finally {
      setCreatingTemplate(false);
    }
  }

  async function updateCategoryName(categoryId: string) {
    if (!editingCategoryName.trim()) {
      toast({
        title: "Nome inválido",
        description: "O nome da categoria não pode estar vazio.",
        variant: "destructive",
      });
      return;
    }

    const category = categories.find(c => c.id === categoryId);
    if (category?.is_system) {
      toast({
        title: "Edição não permitida",
        description: "Categorias do sistema não podem ser editadas.",
        variant: "destructive",
      });
      setEditingCategory(null);
      setEditingCategoryName("");
      setEditingCategoryOrder(0);
      return;
    }

    try {
      const updateData: { name: string; display_order?: number } = {
        name: editingCategoryName.trim(),
      };
      
      if (editingCategoryOrder > 0) {
        updateData.display_order = editingCategoryOrder;
      }

      const { error } = await supabase
        .from('lab_categories')
        .update(updateData)
        .eq('id', categoryId);

      if (error) throw error;

      setEditingCategory(null);
      setEditingCategoryName("");
      setEditingCategoryOrder(0);
      await loadCategories();
      toast({
        title: "Categoria atualizada",
        description: "O nome foi alterado com sucesso.",
      });
    } catch (error) {
      console.error('Error updating category:', error);
      toast({
        title: "Erro ao atualizar categoria",
        variant: "destructive",
      });
    }
  }

  async function deleteCategory(category: Category) {
    if (category.is_system) {
      toast({
        title: "Exclusão não permitida",
        description: "Categorias do sistema não podem ser excluídas.",
        variant: "destructive",
      });
      return;
    }

    // Check if category has subcategories
    if (category.subcategories.length > 0) {
      setDeletingCategory(category);
      return;
    }

    try {
      const { error } = await supabase
        .from('lab_categories')
        .delete()
        .eq('id', category.id);

      if (error) throw error;

      // Select first available category if current was deleted
      const remainingCategories = categories.filter(c => c.id !== category.id);
      if (remainingCategories.length > 0) {
        setSelectedCategoryId(remainingCategories[0].id);
      } else {
        setSelectedCategoryId(null);
      }

      await loadCategories();
      toast({
        title: "Categoria excluída",
        description: "A categoria foi removida com sucesso.",
      });
    } catch (error) {
      console.error('Error deleting category:', error);
      toast({
        title: "Erro ao excluir categoria",
        description: "Não foi possível excluir a categoria.",
        variant: "destructive",
      });
    }
  }

  async function confirmDeleteCategory() {
    if (!deletingCategory) return;

    if (deletingCategory.is_system) {
      toast({
        title: "Exclusão não permitida",
        description: "Categorias do sistema não podem ser excluídas.",
        variant: "destructive",
      });
      setDeletingCategory(null);
      return;
    }

    try {
      // First, delete all subcategories
      for (const subcategory of deletingCategory.subcategories) {
        const { error } = await supabase
          .from('lab_subcategories')
          .delete()
          .eq('id', subcategory.id);

        if (error) {
          console.error('Error deleting subcategory:', error);
          throw error;
        }
      }

      // Then delete the category
      const { error } = await supabase
        .from('lab_categories')
        .delete()
        .eq('id', deletingCategory.id);

      if (error) throw error;

      // Select first available category if current was deleted
      const remainingCategories = categories.filter(c => c.id !== deletingCategory.id);
      if (remainingCategories.length > 0) {
        setSelectedCategoryId(remainingCategories[0].id);
      } else {
        setSelectedCategoryId(null);
      }

      setDeletingCategory(null);
      await loadCategories();
      toast({
        title: "Categoria excluída",
        description: "A categoria e todas as suas subcategorias foram removidas com sucesso.",
      });
    } catch (error) {
      console.error('Error deleting category:', error);
      toast({
        title: "Erro ao excluir categoria",
        description: "Não foi possível excluir a categoria. Ela pode estar sendo usada em resultados.",
        variant: "destructive",
      });
    }
  }

  async function reorderCategories(newOrder: Category[]) {
    try {
      // Update display_order for all categories
      const updates = newOrder.map((category, index) => ({
        id: category.id,
        display_order: index + 1,
      }));

      // Batch update all categories
      for (const update of updates) {
        const { error } = await supabase
          .from('lab_categories')
          .update({ display_order: update.display_order })
          .eq('id', update.id);

        if (error) throw error;
      }

      setCategories([...newOrder]);
      toast({
        title: "Categorias reordenadas",
        description: "A ordem das categorias foi atualizada com sucesso.",
      });
    } catch (error) {
      console.error('Error reordering categories:', error);
      toast({
        title: "Erro ao reordenar categorias",
        description: "Não foi possível atualizar a ordem das categorias.",
        variant: "destructive",
      });
      // Reload to restore original order
      await loadCategories();
    }
  }

  async function moveSubcategoryToCategory(subcategoryId: string, targetCategoryId: string) {
    try {
      const { error } = await supabase
        .from('lab_subcategories')
        .update({ category_id: targetCategoryId })
        .eq('id', subcategoryId);

      if (error) throw error;

      await loadCategories();
      toast({
        title: "Subcategoria movida",
        description: "A subcategoria foi transferida com sucesso.",
      });
    } catch (error) {
      console.error('Error moving subcategory:', error);
      toast({
        title: "Erro ao mover subcategoria",
        description: "Não foi possível mover a subcategoria.",
        variant: "destructive",
      });
      throw error;
    }
  }

  function handleDragStart(event: DragStartEvent) {
    const { active } = event;
    const activeId = active.id as string;
    
    // Check if it's a category or subcategory
    const category = categories.find(cat => cat.id === activeId);
    if (category) {
      setActiveCategory(category);
      setDragType('category');
      return;
    }
    
    // Find the subcategory and its parent category
    for (const cat of categories) {
      const subcategory = cat.subcategories.find(sub => sub.id === activeId);
      if (subcategory) {
        setActiveSubcategory({ ...subcategory, categoryId: cat.id });
        setDragType('subcategory');
        break;
      }
    }
  }

  function handleDragOver(event: DragOverEvent) {
    const { over } = event;
    setOverId(over ? over.id as string : null);
  }

  async function handleDragEnd(event: DragEndEvent) {
    const { active, over } = event;
    
    setActiveSubcategory(null);
    setActiveCategory(null);
    setOverId(null);
    setDragType(null);

    if (!over) return;

    const activeId = active.id as string;
    const overId = over.id as string;

    // Handle category reordering
    if (dragType === 'category') {
      const oldIndex = categories.findIndex(cat => cat.id === activeId);
      const newIndex = categories.findIndex(cat => cat.id === overId);

      if (oldIndex !== -1 && newIndex !== -1 && oldIndex !== newIndex) {
        const newOrder = arrayMove(categories, oldIndex, newIndex) as Category[];
        await reorderCategories(newOrder);
      }
      return;
    }

    // Handle subcategory moving between categories
    if (dragType === 'subcategory') {
      const subcategoryId = activeId;
      const targetCategoryId = overId;

      // Find current category
      const currentCategory = categories.find(cat =>
        cat.subcategories.some(sub => sub.id === subcategoryId)
      );

      if (!currentCategory || currentCategory.id === targetCategoryId) {
        return;
      }

      // Validate target is not a system category
      const targetCategory = categories.find(c => c.id === targetCategoryId);
      if (targetCategory?.is_system) {
        toast({
          title: "Ação não permitida",
          description: "Não é possível mover para categorias do sistema.",
          variant: "destructive",
        });
        return;
      }

      await moveSubcategoryToCategory(subcategoryId, targetCategoryId);
    }
  }

  const categoryIds = categories.map(cat => cat.id);

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCenter}
      onDragStart={handleDragStart}
      onDragOver={handleDragOver}
      onDragEnd={handleDragEnd}
    >
      <div className="space-y-4 sm:space-y-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
          <div>
            <PageTitle>Categorias de Saúde</PageTitle>
            <p className="text-sm text-muted-foreground mt-1">
              Gerencie categorias e subcategorias.
            </p>
          </div>
          <div className="flex gap-2">
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  variant="outline"
                  disabled={creatingTemplate || addingCategory}
                >
                  {creatingTemplate ? (
                    <>
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      Criando...
                    </>
                  ) : (
                    <>
                      <Sparkles className="h-4 w-4 mr-2" />
                      Criar Template
                    </>
                  )}
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                {loadingTemplates ? (
                  <DropdownMenuItem disabled>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Carregando templates...
                  </DropdownMenuItem>
                ) : templates.length === 0 ? (
                  <DropdownMenuItem disabled>
                    <FileText className="h-4 w-4 mr-2" />
                    Nenhum template disponível
                  </DropdownMenuItem>
                ) : (
                  templates.map((template) => (
                    <DropdownMenuItem
                      key={template.id}
                      onClick={() => createCategoryFromTemplate(template.id)}
                      disabled={creatingTemplate}
                    >
                      <FileText className="h-4 w-4 mr-2" />
                      {template.title}
                    </DropdownMenuItem>
                  ))
                )}
              </DropdownMenuContent>
            </DropdownMenu>
            <Button
              onClick={() => {
                setAddingCategory(true);
                setNewCategoryName("");
                setNewCategoryOrder(0);
              }}
              disabled={addingCategory || creatingTemplate}
            >
              <Plus className="h-4 w-4 mr-2" />
              Nova Categoria
            </Button>
          </div>
        </div>

        <Alert className="overflow-hidden max-w-[320px] sm:w-full sm:max-w-none">
          <BarChart3 className="h-4 w-4 shrink-0" />
          <AlertDescription className="text-xs break-words leading-relaxed">
            Marque "Exibir gráfico" nas subcategorias para acompanhar visualmente.
          </AlertDescription>
        </Alert>

        {loading && categories.length === 0 ? (
          <div className="flex flex-col lg:flex-row gap-6">
            {/* Sidebar Skeleton */}
            <div className="lg:sticky lg:top-6 lg:self-start shrink-0">
              {/* Mobile: Badges skeleton */}
              <div className="lg:hidden">
                <div className="flex gap-2 overflow-x-auto pb-2 -mx-4 px-4 sm:-mx-6 sm:px-6">
                  {[1, 2, 3, 4, 5].map((i) => (
                    <Skeleton key={i} className="h-8 w-24 shrink-0" />
                  ))}
                </div>
              </div>
              
              {/* Desktop: Sidebar skeleton */}
              <div className="hidden lg:block lg:w-64 bg-card border rounded-xl p-2 shadow-sm">
                <div className="flex flex-col gap-1">
                  {[1, 2, 3, 4, 5].map((i) => (
                    <Skeleton key={i} className="h-12 w-full" />
                  ))}
                </div>
              </div>
            </div>
            
            {/* Content Skeleton */}
            <div className="flex-1 min-w-0 w-full lg:w-auto min-h-[600px]">
              <Card>
                <CardHeader>
                  <Skeleton className="h-6 w-48 mb-2" />
                  <Skeleton className="h-4 w-32" />
                </CardHeader>
                <CardContent className="space-y-3">
                  {[1, 2, 3, 4].map((i) => (
                    <Skeleton key={i} className="h-16 w-full" />
                  ))}
                </CardContent>
              </Card>
            </div>
          </div>
        ) : categories.length === 0 ? (
          <Card>
            <CardContent className="py-8">
              <p className="text-center text-muted-foreground">
                Nenhuma categoria encontrada. Complete o processo de onboarding para criar suas categorias.
              </p>
            </CardContent>
          </Card>
        ) : (
          <>
            {justCreatedCategoryId && (
              <Alert className="border-green-500 bg-green-50 dark:bg-green-950 animate-fade-in">
                <CheckCircle2 className="h-4 w-4 text-green-600" />
                <AlertTitle>Categoria criada com sucesso!</AlertTitle>
                <AlertDescription>
                  Agora você pode adicionar subcategorias e usá-la nos resultados dos pacientes.
                </AlertDescription>
              </Alert>
            )}

            {addingCategory && (
              <Card className="border-primary animate-fade-in">
                <CardHeader>
                  <CardTitle>Nova Categoria</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div>
                    <label className="text-sm font-medium mb-2 block">Nome</label>
                    <Input
                      value={newCategoryName}
                      onChange={(e) => setNewCategoryName(e.target.value)}
                      placeholder="Nome da categoria"
                      autoFocus
                      disabled={creatingCategory}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' && !creatingCategory) createCategory();
                        if (e.key === 'Escape') setAddingCategory(false);
                      }}
                    />
                  </div>
                  <div>
                    <label className="text-sm font-medium mb-2 block">Ordem (opcional)</label>
                    <Input
                      type="number"
                      value={newCategoryOrder || ''}
                      onChange={(e) => setNewCategoryOrder(parseInt(e.target.value) || 0)}
                      placeholder="Deixe vazio para adicionar ao final"
                      disabled={creatingCategory}
                    />
                  </div>
                  <div className="flex gap-2">
                    <Button onClick={createCategory} disabled={creatingCategory}>
                      {creatingCategory ? (
                        <>
                          <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                          Criando...
                        </>
                      ) : (
                        <>
                          <Check className="h-4 w-4 mr-2" />
                          Criar
                        </>
                      )}
                    </Button>
                    <Button 
                      variant="ghost" 
                      onClick={() => {
                        setAddingCategory(false);
                        setNewCategoryName("");
                        setNewCategoryOrder(0);
                      }}
                      disabled={creatingCategory}
                    >
                      <X className="h-4 w-4 mr-2" />
                      Cancelar
                    </Button>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Layout com Badges scrolláveis */}
            <div className="flex flex-col lg:flex-row gap-6">
              {/* Sidebar - Mobile: Badges horizontais com scroll / Desktop: Lista vertical */}
              <div className="lg:sticky lg:top-6 lg:self-start shrink-0">
                <SortableContext items={categoryIds} strategy={verticalListSortingStrategy}>
                  {/* Mobile: Scroll horizontal com badges */}
                  <div className="lg:hidden">
                    <div className="flex gap-2 overflow-x-auto pb-2 -mx-4 px-4 sm:-mx-6 sm:px-6 max-w-[360px]">
                      {categories.map((category) => (
                        <DraggableCategoryBadge
                          key={category.id}
                          category={category}
                          isSelected={selectedCategoryId === category.id}
                          onClick={() => setSelectedCategoryId(category.id)}
                        />
                      ))}
                    </div>
                  </div>

                  {/* Desktop: Sidebar vertical */}
                  <div className="hidden lg:block lg:w-64 bg-card border rounded-xl p-2 shadow-sm">
                    <div className="flex flex-col gap-1">
                      {categories.map((category) => (
                        <DraggableCategoryItem
                          key={category.id}
                          category={category}
                          isSelected={selectedCategoryId === category.id}
                          onClick={() => setSelectedCategoryId(category.id)}
                        />
                      ))}
                    </div>
                  </div>
                </SortableContext>
              </div>

              {/* Conteúdo */}
              <div className="flex-1 min-w-0 w-full lg:w-auto min-h-[600px]">
                {categories.map((category) => (
                  selectedCategoryId === category.id && (
                    <DroppableCategory 
                      key={category.id}
                      category={category}
                      isOver={overId === category.id}
                    >
                      <CardHeader>
                        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                          {editingCategory === category.id ? (
                            <div className="flex flex-col sm:flex-row gap-2 flex-1 min-w-0">
                              <Input
                                value={editingCategoryName}
                                onChange={(e) => setEditingCategoryName(e.target.value)}
                                className="flex-1 min-w-0"
                                placeholder="Nome da categoria"
                                autoFocus
                                onKeyDown={(e) => {
                                  if (e.key === 'Enter') updateCategoryName(category.id);
                                  if (e.key === 'Escape') {
                                    setEditingCategory(null);
                                    setEditingCategoryName("");
                                    setEditingCategoryOrder(0);
                                  }
                                }}
                              />
                              <Input
                                type="number"
                                value={editingCategoryOrder || ''}
                                onChange={(e) => setEditingCategoryOrder(parseInt(e.target.value) || 0)}
                                className="w-24"
                                placeholder="Ordem"
                              />
                              <Button
                                size="sm"
                                onClick={() => updateCategoryName(category.id)}
                              >
                                <Check className="h-4 w-4" />
                              </Button>
                              <Button
                                size="sm"
                                variant="ghost"
                                onClick={() => {
                                  setEditingCategory(null);
                                  setEditingCategoryName("");
                                  setEditingCategoryOrder(0);
                                }}
                              >
                                <X className="h-4 w-4" />
                              </Button>
                            </div>
                          ) : (
                            <>
                              <div className="min-w-0">
                                <CardTitle className="flex items-center gap-2 flex-wrap">
                                  <span className="truncate">{category.name}</span>
                                  {category.is_system && (
                                    <span className="text-xs font-normal text-muted-foreground px-2 py-1 bg-muted rounded shrink-0">
                                      Sistema
                                    </span>
                                  )}
                                </CardTitle>
                                <CardDescription>
                                  {category.subcategories.length} subcategoria(s)
                                </CardDescription>
                              </div>
                              {!category.is_system && (
                                <div className="flex gap-1 shrink-0 flex-wrap">
                                  <Button
                                    size="sm"
                                    variant="ghost"
                                    onClick={() => {
                                      setEditingCategory(category.id);
                                      setEditingCategoryName(category.name);
                                      setEditingCategoryOrder(category.display_order);
                                    }}
                                  >
                                    <Edit2 className="h-4 w-4" />
                                  </Button>
                                  <Button
                                    size="sm"
                                    variant="ghost"
                                    onClick={() => deleteCategory(category)}
                                  >
                                    <Trash2 className="h-4 w-4" />
                                  </Button>
                                </div>
                              )}
                            </>
                          )}
                        </div>
                      </CardHeader>
                      <CardContent className="space-y-3">
                        {overId === category.id && !category.is_system && (
                          <Alert className="bg-primary/10 border-primary">
                            <AlertDescription className="text-sm font-medium">
                              Solte aqui para mover a subcategoria
                            </AlertDescription>
                          </Alert>
                        )}
                        
                        {loading ? (
                          // Skeleton apenas na área de conteúdo dinâmico
                          <>
                            {[1, 2, 3, 4].map((i) => (
                              <Skeleton key={i} className="h-16 w-full" />
                            ))}
                          </>
                        ) : category.subcategories.length === 0 ? (
                          <div className="text-center py-8 text-muted-foreground">
                            <p className="mb-4">Nenhuma subcategoria encontrada.</p>
                          </div>
                        ) : (
                          category.subcategories.map(sub => (
                            <DraggableSubcategory
                              key={sub.id}
                              subcategory={sub}
                              categoryId={category.id}
                              isOver={overId === category.id}
                              editingSubcategory={editingSubcategory}
                              editingName={editingName}
                              setEditingName={setEditingName}
                              setEditingSubcategory={setEditingSubcategory}
                              updateSubcategoryName={updateSubcategoryName}
                              toggleShowChart={toggleShowChart}
                              deleteSubcategory={deleteSubcategory}
                            />
                          ))
                        )}

                        {!loading && (
                          addingToCategory === category.id ? (
                            <div className="flex items-center gap-2 p-3 border rounded-lg bg-accent/30">
                              <Input
                                value={newSubcategoryName}
                                onChange={(e) => setNewSubcategoryName(e.target.value)}
                                placeholder="Nome da nova subcategoria"
                                className="flex-1"
                                autoFocus
                                onKeyDown={(e) => {
                                  if (e.key === 'Enter') addSubcategory(category.id);
                                  if (e.key === 'Escape') setAddingToCategory(null);
                                }}
                              />
                              <Button
                                size="sm"
                                onClick={() => addSubcategory(category.id)}
                              >
                                <Check className="h-4 w-4" />
                              </Button>
                              <Button
                                size="sm"
                                variant="ghost"
                                onClick={() => {
                                  setAddingToCategory(null);
                                  setNewSubcategoryName("");
                                }}
                              >
                                <X className="h-4 w-4" />
                              </Button>
                            </div>
                          ) : (
                            <Button
                              variant="outline"
                              size="sm"
                              className="w-full"
                              onClick={() => setAddingToCategory(category.id)}
                            >
                              <Plus className="h-4 w-4 mr-2" />
                              Adicionar subcategoria
                            </Button>
                          )
                        )}
                      </CardContent>
                    </DroppableCategory>
                  )
                ))}
              </div>
            </div>
          </>
        )}

        {/* AlertDialog de confirmação de exclusão */}
        <AlertDialog open={!!deletingCategory} onOpenChange={(open) => !open && setDeletingCategory(null)}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Confirmar exclusão</AlertDialogTitle>
              <AlertDialogDescription>
                Tem certeza que deseja excluir a categoria "{deletingCategory?.name}"?
                {deletingCategory && deletingCategory.subcategories.length > 0 && (
                  <span className="block mt-2 font-semibold text-destructive">
                    Esta ação também excluirá todas as {deletingCategory.subcategories.length} subcategoria(s) associadas. Esta ação não pode ser desfeita.
                  </span>
                )}
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Cancelar</AlertDialogCancel>
              <AlertDialogAction
                onClick={confirmDeleteCategory}
                className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              >
                Excluir
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
      
      <DragOverlay>
        {activeCategory && (
          <div className="flex items-center gap-2 px-3 py-2 border rounded-lg bg-background shadow-lg opacity-90">
            <GripVertical className="h-4 w-4 text-muted-foreground shrink-0" />
            <Folder className="h-4 w-4 shrink-0" />
            <span className="font-medium truncate">{activeCategory.name}</span>
          </div>
        )}
        {activeSubcategory && (
          <div className="flex items-center gap-2 p-3 border rounded-lg bg-background shadow-lg opacity-90 max-w-[300px]">
            <GripVertical className="h-4 w-4 text-muted-foreground shrink-0" />
            <span className="font-medium truncate">{activeSubcategory.name}</span>
          </div>
        )}
      </DragOverlay>
    </DndContext>
  );
}
